import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  StyledMainImage,
  StyledUnitScreen,
  StyledTag,
  StickyBottomBox,
  CalendarWrapper,
} from "./styles";

import assets from "../../assets/index";
import {
  Button,
  Divider,
  ErrorScreen,
  FlexDiv,
  LoaderComponent,
} from "../../components/index";

import {
  useGetUnitAvailability,
  useGetUnitDetails,
  useWindowSize,
} from "../../hooks";
import { amenityIcons } from "./amenitiesIcons";

import Calendar from "react-calendar";

import { Value as CalendarValue } from "react-calendar/dist/cjs/shared/types";

import BookingConfirmationContainer from "../../containers/BookingConfirmationContainer";
import { ThemeContext } from "../../contexts/ThemeContext";
import ImageSlider from "../../components/ImageSlider";

const SingleUnitScreen = () => {
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { unit: unitData, loading, error } = useGetUnitDetails(id || "");
  const { isSm } = useWindowSize();
  const navigate = useNavigate();

  const [showFullDescription, setShowFullDescription] = useState(false);

  // Extract dates from query parameters
  const [fromDate, setFromDate] = useState<Date | null>(
    searchParams.get("fromDate")
      ? new Date(searchParams.get("fromDate")!)
      : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    searchParams.get("toDate") ? new Date(searchParams.get("toDate")!) : null
  );

  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const themeContext = useContext(ThemeContext);

  if (!themeContext) {
    throw new Error("ThemeContext must be used within a ThemeProvider");
  }

  const { themeMode } = themeContext;

  const toggleDescription = () => {
    setShowFullDescription((prev) => !prev);
  };

  const [currentMonth, setCurrentMonth] = useState<Date>(
    fromDate || new Date()
  );

  // Extract the visible month's start and end dates
  const startOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const endOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  );

  const formattedStartOfMonth = startOfMonth.toISOString().split("T")[0];
  const formattedEndOfMonth = endOfMonth.toISOString().split("T")[0];

  const { unitAvailability, loading: availabilitiesLoadiing } =
    useGetUnitAvailability(
      id || "",
      formattedStartOfMonth,
      formattedEndOfMonth
    );

  const isDateAvailable = (date: Date): boolean => {
    const formattedDateStr = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];

    if (unitAvailability) {
      return unitAvailability[formattedDateStr]?.availability > 0;
    }
    return true;
  };
  const getDatesBetween = (startDate: Date, endDate: Date): Date[] => {
    const dates: Date[] = [];
    let currentDate = new Date(
      Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      )
    );

    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    return dates;
  };
  const isRangeAvailable = (startDate: Date, endDate: Date): boolean => {
    const dates = getDatesBetween(startDate, endDate);
    return dates.every((date) => isDateAvailable(date));
  };

  const handleDateSelect = (value: CalendarValue) => {
    setDateRangeError(null);
    if (Array.isArray(value)) {
      const [startDate, endDate] = value;

      if (startDate && endDate && isRangeAvailable(startDate, endDate)) {
        setFromDate(startDate);
        setEndDate(endDate);
      } else {
        console.warn("Selected range contains unavailable dates.");
      }
    } else if (value instanceof Date && isDateAvailable(value)) {
      setFromDate(value);
      setEndDate(null);
    } else {
      setFromDate(null);
      setEndDate(null);
    }
  };

  const tileDisabled = ({ date }: { date: Date }): boolean => {
    const formattedDateStr = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];

    return unitAvailability?.[formattedDateStr]?.availability === 0;
  };

  const tileClassName = ({
    date,
    view,
  }: {
    date: Date;
    view: string;
  }): string => {
    if (view !== "month") return "";

    if (fromDate && endDate && date >= fromDate && date <= endDate) {
      return "in-range";
    }

    return isDateAvailable(date) ? "available" : "unavailable";
  };

  const tileContent = ({ date }: { date: Date }) => {
    const formattedDateStr = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
      .toISOString()
      .split("T")[0];

    const availabilityInfo = unitAvailability?.[formattedDateStr];

    if (availabilityInfo) {
      return (
        <div
          style={{
            textAlign: "center",
            position: "relative",
          }}
        >
          <span
            style={{
              fontSize: "9px",
              position: "absolute",
              display: "block",
              top: "-14px",
              right: "2px",
              left: "2px",
            }}
          >
            {availabilityInfo.price ? `$${availabilityInfo.price}` : "-"}
          </span>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    // Ensure the calendar range is properly set when the component loads
    if (searchParams.get("fromDate") && searchParams.get("toDate")) {
      setFromDate(new Date(searchParams.get("fromDate")!));
      setEndDate(new Date(searchParams.get("toDate")!));
    }
  }, [searchParams]);

  const handleMonthChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (activeStartDate) {
      setCurrentMonth(activeStartDate);
    }
  };

  if (loading) {
    return <LoaderComponent content="Unit details loading ..." />;
  }

  if (error || !unitData)
    return (
      <ErrorScreen
        content={"An error happened. Try again"}
        imageSrc={assets.NoResultsVector}
      />
    );
  console.log(isSm);
  if (unitData) {
    const isDescriptionLong =
      unitData.description && unitData.description?.length > 200;
    const descriptionPreview = isDescriptionLong
      ? `${unitData.description && unitData.description.substring(0, 200)}...`
      : unitData.description;
    const checkEventsAllowed =
      unitData.inUseFeatures?.suitableEvents === true
        ? "🎉\u00A0\u00A0Events are allowed in this unit"
        : "🚫\u00A0\u00A0Events organized in the unit are not allowed";

    const checkPetsAllowed =
      unitData.inUseFeatures?.suitablePets === true
        ? `🐶\u00A0\u00A0Pets are allowed in this unit. You can bring a maximum of ${unitData.inUseFeatures.petCapacity} pet.`
        : "🚫\u00A0\u00A0Pets are not allowed in the unit";

    const checkSmokingAllowed =
      unitData.inUseFeatures?.suitableSmocking === true
        ? "🚬\u00A0\u00A0Smoking is allowed."
        : "🚭\u00A0\u00A0Smoking is not allowed.";

    const checkPhotoAllowed =
      unitData.inUseFeatures?.commercialPhotographyAllowed === true
        ? "📸\u00A0\u00A0Photographing and filming allowed"
        : "🚫\u00A0\u00A0Photographing and filming is not allowed.";

    const placeholderImage =
      themeMode === "dark"
        ? assets.imagePlaceholderDark
        : assets.imagePlaceholderWhite;

    if (showBookingConfirmation === true) {
      return (
        <BookingConfirmationContainer
          fromDate={fromDate}
          endDate={endDate}
          unitData={unitData}
          switchOffFunction={() => setShowBookingConfirmation(false)}
        />
      );
    }

    const handleBookNowClick = () => {
      setDateRangeError(null);

      if (!fromDate || !endDate) {
        setDateRangeError("❗ Please select both check-in and check-out dates.");
        return;
      }

      const minNights = unitData.ratePlanInfo?.reservationMinDays;
      const maxNights = unitData.ratePlanInfo?.reservationMaxDays;

      const diffTime = endDate.getTime() - fromDate.getTime();
      const diffNights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (typeof minNights === "number" && minNights > 0 && diffNights < minNights) {
        setDateRangeError(`❗ Minimum stay required is ${minNights} nights.`);
        return;
      }

      if (typeof maxNights === "number" && maxNights > 0 && diffNights > maxNights) {
        setDateRangeError(`❗ Maximum stay allowed is ${maxNights} nights.`);
        return;
      }

      setShowBookingConfirmation(true);
    };

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    return (
      <StyledUnitScreen>
        {unitData?.images?.length === 0 && !isSm ? (
          <StyledMainImage
            src={unitData.mainImage?.image?.urls?.big || placeholderImage}
            onError={(e) => {
              (e.target as HTMLImageElement).src = placeholderImage;
            }}
          />
        ) : (
          <ImageSlider slides={unitData.images} />
        )}
        <span className="go-back" onClick={() => navigate(-1)}>
          <img src={assets.GoBack} alt="go back icon" />
        </span>{" "}
        <div>
          <h2> {unitData.listingTitle}</h2>
          <h4>
            {unitData.inUseCity},{unitData.inUseRegion}
          </h4>
        </div>
        <FlexDiv gap="10px" style={{ margin: "10px 0" }}>
          <StyledTag>
            <img src={assets.GuestIcon} alt="guest icon" />{" "}
            {unitData.inUseFeatures?.accommodates || "None"}
          </StyledTag>
          <StyledTag>
            <img src={assets.BedIcon} alt="bed icon" />{" "}
            {unitData.inUseFeatures?.bedrooms || "None"}
          </StyledTag>
          <StyledTag>
            <img src={assets.BathIcon} alt="bath icon" />{" "}
            {unitData.inUseFeatures?.bathrooms || "None"}
          </StyledTag>
        </FlexDiv>
        <div>
          {" "}
          <p className="description">
            {showFullDescription ? unitData.description : descriptionPreview}
          </p>
          {isDescriptionLong && (
            <Button onClick={toggleDescription} className="outlined-btn">
              {showFullDescription ? "Show Less" : "Show More"}
            </Button>
          )}
        </div>
        <Divider content="Amenities" margin="20px 0 20px 0" />
        <div>
          {unitData.amenities?.length ? (
            unitData.amenities.map((amenity) => (
              <div key={amenity.uid} className="amenity-item">
                <span className="icon">
                  {amenityIcons[amenity.amenity.description] || "✔️"}
                </span>
                <span>{amenity.amenity.title}</span>
              </div>
            ))
          ) : (
            <p className="description">No amenities were added.</p>
          )}
        </div>
        <Divider content="House Rules" margin="20px 0 20px 0" />
        <div>
          <p className="description">{unitData.houseRules}</p>
          <p className="description"> {checkEventsAllowed}</p>
          <p className="description"> {checkPetsAllowed}</p>
          <p className="description"> {checkSmokingAllowed}</p>
          <p className="description">{checkPhotoAllowed}</p>
        </div>
        {unitData.houseManual !== null && (
          <>
            {" "}
            <Divider content="House Manual" margin="20px 0 20px 0" />{" "}
            <p className="description">{unitData.houseManual}</p>
          </>
        )}
        <Divider content="Booking Policy" margin="20px 0 20px 0" />
        <p className="description">
          {unitData.bookingPolicy?.refundPolicy ? (
            <>
              This unit offers a{" "}
              <strong>{unitData.bookingPolicy.refundPolicy.name}</strong> refund
              policy
              {unitData.bookingPolicy.refundPolicy.deadline1Offset !==
                undefined &&
                unitData.bookingPolicy.refundPolicy.deadline1RefundPercentage !==
                undefined
                ? `, allowing you to cancel up to ${unitData.bookingPolicy.refundPolicy.deadline1Offset} days before arrival and receive a ${unitData.bookingPolicy.refundPolicy.deadline1RefundPercentage}% refund.`
                : unitData.bookingPolicy.refundPolicy.deadline1Offset !==
                  undefined
                  ? `, allowing cancellations up to ${unitData.bookingPolicy.refundPolicy.deadline1Offset} days before arrival.`
                  : unitData.bookingPolicy.refundPolicy
                    .deadline1RefundPercentage !== undefined
                    ? `, offering a ${unitData.bookingPolicy.refundPolicy.deadline1RefundPercentage}% refund upon cancellation.`
                    : "."}
            </>
          ) : (
            "This unit does not offer a refund policy at the moment."
          )}
        </p>
        <Divider content="Book your stay" />
        <FlexDiv gap="10px">
          {" "}
          <StyledTag>
            Minimum stay {unitData.ratePlanInfo?.reservationMinDays || "N/A"}{" "}
            days
          </StyledTag>
          <StyledTag>
            Maximum stay {unitData.ratePlanInfo?.reservationMaxDays || "N/A"}{" "}
            days
          </StyledTag>
        </FlexDiv>
        <CalendarWrapper>
          {availabilitiesLoadiing ? (
            <LoaderComponent content="Availabilities loading ..." isRelative />
          ) : (
            <Calendar
              onChange={handleDateSelect}
              value={fromDate && endDate ? [fromDate, endDate] : fromDate}
              selectRange
              minDate={tomorrow}
              tileDisabled={tileDisabled}
              tileContent={tileContent}
              tileClassName={tileClassName}
              onActiveStartDateChange={handleMonthChange}
              activeStartDate={currentMonth}
            />
          )}
        </CalendarWrapper>
        <StickyBottomBox>
          {dateRangeError && (
            <p style={{ color: "#FF5722", textAlign: "center", marginTop: "10px" }}>
              {dateRangeError}
            </p>
          )}
          <Button onClick={handleBookNowClick}>
            Book Now <img src={assets.ArrowRightIcon} alt="arrow-forward" />
          </Button>
        </StickyBottomBox>
      </StyledUnitScreen>
    );
  }
  // Fallback return if none of the above conditions are met
  return null;
};


export default SingleUnitScreen;
